import { useMemo, useState } from 'react';
import { ThemeButton } from './ThemeWrapper';
import { disksForHosts, formatLastSeen, formatLoad, parsePercent, usageTone } from '../domain/infra/hostMetrics';

const TONE_COLOR = {
  ok: '#15803d',
  warn: '#ca8a04',
  high: '#b45309',
  neutral: 'var(--color-border)',
};

function UsageMeter({ value }) {
  const pct = parsePercent(value);
  if (pct == null) return <span className="theme-text-secondary">—</span>;
  const tone = usageTone(pct);
  return (
    <div className="flex items-center gap-2 min-w-[7.5rem]">
      <div className="h-2 w-16 overflow-hidden rounded" style={{ backgroundColor: 'var(--color-border)' }}>
        <div
          className="h-full rounded"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: TONE_COLOR[tone] }}
        />
      </div>
      <span className="tabular-nums text-xs theme-text">{pct.toFixed(1)}%</span>
    </div>
  );
}

function sshLabel(host) {
  const status = String(host.sshStatus || host.discovery?.ssh || '').toUpperCase();
  if (status === 'SKIPPED') return 'SKIPPED';
  if (host.sshOk === true || status === 'OK') return 'OK';
  if (host.sshOk === false || status === 'FAILED') return 'FAILED';
  return '—';
}

export function LabHostOverviewTable({ hosts, suggestion, discoveredAt }) {
  if (!hosts?.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm theme-text">
        <thead>
          <tr className="text-left theme-text-secondary">
            <th className="pr-4 py-1">Host</th>
            <th className="pr-4 py-1">OS / kernel</th>
            <th className="pr-4 py-1">CPU %</th>
            <th className="pr-4 py-1">RAM %</th>
            <th className="pr-4 py-1">Load avg (1m / 5m / 15m)</th>
            <th className="pr-4 py-1">Uptime</th>
            <th className="pr-4 py-1">Last seen</th>
            <th className="pr-4 py-1">SSH</th>
            <th className="pr-4 py-1">SMS accounts</th>
            <th className="pr-4 py-1">Exception</th>
          </tr>
        </thead>
        <tbody>
          {hosts.map((host) => {
            const suggested = suggestion?.host === host.host;
            return (
              <tr key={`${host.alias}-${host.host}`} className={suggested ? 'font-medium' : ''}>
                <td className="pr-4 py-2 align-top">
                  <div>{host.alias}</div>
                  <div className="text-xs theme-text-secondary font-normal">{host.fqdn || host.host || '—'}</div>
                </td>
                <td className="pr-4 py-2 align-top">
                  <div>{host.os || '—'}</div>
                  {host.kernel ? <div className="text-xs theme-text-secondary font-normal">{host.kernel}</div> : null}
                </td>
                <td className="pr-4 py-2 align-top">
                  <UsageMeter value={host.cpuPct} />
                </td>
                <td className="pr-4 py-2 align-top">
                  <UsageMeter value={host.ramPct} />
                  {host.ramUsed || host.ram ? (
                    <div className="text-xs theme-text-secondary font-normal">
                      {host.ramUsed || '—'} / {host.ram || '—'}
                    </div>
                  ) : null}
                </td>
                <td className="pr-4 py-2 align-top">{formatLoad(host) || '—'}</td>
                <td className="pr-4 py-2 align-top">{host.uptime || '—'}</td>
                <td className="pr-4 py-2 align-top">{formatLastSeen(host.checkedAt || discoveredAt) || '—'}</td>
                <td className="pr-4 py-2 align-top">{sshLabel(host)}</td>
                <td className="pr-4 py-2 align-top">{host.accountCount}</td>
                <td className="pr-4 py-2 align-top text-red-700">{host.sshError || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function LabHostDiskTable({ hosts, discoveredAt }) {
  const [alias, setAlias] = useState('');
  const aliases = useMemo(
    () => [...new Set(hosts.filter((host) => (host.disks || []).length).map((host) => host.alias))],
    [hosts]
  );
  const disks = useMemo(() => disksForHosts(hosts, alias), [hosts, alias]);
  if (!aliases.length) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium theme-text">Disks</h3>
      <div className="flex flex-wrap gap-2">
        <ThemeButton type="button" size="sm" variant={alias ? 'outline' : undefined} onClick={() => setAlias('')}>
          All hosts
        </ThemeButton>
        {aliases.map((name) => (
          <ThemeButton
            key={name}
            type="button"
            size="sm"
            variant={alias === name ? undefined : 'outline'}
            onClick={() => setAlias(name)}
          >
            {name}
          </ThemeButton>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm theme-text">
          <thead>
            <tr className="text-left theme-text-secondary">
              <th className="pr-4 py-1">Host</th>
              <th className="pr-4 py-1">Device</th>
              <th className="pr-4 py-1">Mount</th>
              <th className="pr-4 py-1">Type</th>
              <th className="pr-4 py-1">Total</th>
              <th className="pr-4 py-1">Used</th>
              <th className="pr-4 py-1">Avail</th>
              <th className="pr-4 py-1">Usage</th>
              <th className="pr-4 py-1">Checked</th>
            </tr>
          </thead>
          <tbody>
            {disks.map((disk) => (
              <tr key={`${disk.alias}-${disk.device}-${disk.mount}`}>
                <td className="pr-4 py-2">{disk.alias}</td>
                <td className="pr-4 py-2 font-mono text-xs">{disk.device}</td>
                <td className="pr-4 py-2">{disk.mount}</td>
                <td className="pr-4 py-2">{disk.type || '—'}</td>
                <td className="pr-4 py-2">{disk.total || '—'}</td>
                <td className="pr-4 py-2">{disk.used || '—'}</td>
                <td className="pr-4 py-2">{disk.avail || '—'}</td>
                <td className="pr-4 py-2">
                  <UsageMeter value={disk.usage} />
                </td>
                <td className="pr-4 py-2">{formatLastSeen(disk.checkedAt || discoveredAt) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
